import * as Sentry from "@sentry/react";
import type { Breadcrumb, ErrorEvent, EventHint } from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  isRouteErrorResponse,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

import { apiRouteTemplates } from "@/api/routeTemplates";
import { isHttpError } from "@/helpers/Network/errors";

const signalRHubPathPattern = /(?:^|\/)hubs\/[^/]+(?:\/|$)/;

const httpEndpointPatterns = [...apiRouteTemplates, "/graphql"].map((template) => ({
  endpoint: template.replace(/\{[^}]+\}/g, ":param"),
  pattern: new RegExp(
    `^${template
      .split(/(\{[^}]+\})/)
      .map((part) => (part.startsWith("{") ? "[^/]+" : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")))
      .join("")}/?$`,
  ),
}));

function normalizeHttpUrl(
  rawUrl: string | undefined,
  apiBaseUrl: string | undefined,
): { url?: string; endpoint?: string } {
  if (!rawUrl) {
    return {};
  }
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    let pathname = parsed.pathname;
    let basePath = "";
    if (apiBaseUrl) {
      const apiBase = new URL(apiBaseUrl, window.location.origin);
      const prefix = apiBase.pathname.replace(/\/+$/, "");
      if (parsed.origin === apiBase.origin && prefix && pathname.startsWith(`${prefix}/`)) {
        basePath = prefix;
        pathname = pathname.slice(prefix.length).replace(/^\/+/, "/");
      }
    }
    const endpoint =
      httpEndpointPatterns.find((route) => route.pattern.test(pathname))?.endpoint ??
      (pathname.startsWith("/api/") ? "/api/unknown" : "/unknown");

    return { url: `${parsed.origin}${basePath}${endpoint}`, endpoint };
  } catch {
    return { endpoint: "/unknown" };
  }
}

function matchSentryRoutes(...args: Parameters<typeof matchRoutes>): ReturnType<typeof matchRoutes> {
  const matches = matchRoutes(...args);
  const lastMatch = matches?.at(-1);
  if (!matches || !lastMatch) {
    return null;
  }

  // Give Sentry one full route, resolving our absolute module paths and nested /* parents.
  let path = "";
  for (const match of matches) {
    const segment = match.route.path?.replace(/\/\*$/, "");
    if (segment) {
      path = segment.startsWith("/") ? segment : `${path}/${segment}`;
    }
  }

  return [{ ...lastMatch, route: { path: path || "/" } }];
}

function responseMessage(payload: unknown): string | undefined {
  if (typeof payload === "string") {
    return payload.slice(0, 1000);
  }
  if (payload instanceof Error) {
    return payload.message;
  }
  if (payload && typeof payload === "object") {
    const details = payload as Record<string, unknown>;
    for (const key of ["detail", "message", "title", "error"] as const) {
      const value = details[key];
      if (typeof value === "string") {
        return value.slice(0, 1000);
      }
    }
  }

  return undefined;
}

async function readResponseError(response: Response): Promise<unknown> {
  let text: string;
  try {
    text = (await response.clone().text()).slice(0, 4000);
  } catch {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function enrichSentryError(event: ErrorEvent, hint: EventHint): Promise<ErrorEvent> {
  const error = hint.originalException;

  if (error instanceof Response || isHttpError(error) || isRouteErrorResponse(error)) {
    const status = error.status;
    const statusText = error.statusText;
    const rawUrl = "url" in error ? error.url.split(/[?#]/)[0] : undefined;
    const apiBaseUrl =
      "requestBaseUrl" in error && typeof error.requestBaseUrl === "string" ? error.requestBaseUrl : undefined;
    const { url, endpoint } = normalizeHttpUrl(rawUrl, apiBaseUrl);
    const method =
      "requestMethod" in error && typeof error.requestMethod === "string"
        ? error.requestMethod.toUpperCase()
        : undefined;
    let payload: unknown;
    if (isRouteErrorResponse(error)) {
      payload = error.data;
    } else if ("error" in error) {
      payload = error.error;
    } else if (error instanceof Response) {
      payload = await readResponseError(error);
    }
    const serverMessage = responseMessage(payload);
    const message = [`HTTP ${status} ${statusText || ""}`.trim(), url, serverMessage].filter(Boolean).join(": ");
    // Preserve the SDK's mechanism and capture stack, including unhandled rejections.
    const exceptions = event.exception?.values ?? [];

    return {
      ...event,
      ...(status === 404 ? { level: "warning" } : {}),
      exception: {
        ...event.exception,
        values: [...exceptions.slice(0, -1), { ...exceptions.at(-1), type: "HttpError", value: message }],
      },
      message,
      tags: { ...event.tags, http_status: status, ...(method ? { http_method: method } : {}) },
      fingerprint: [
        // The default hash can include record IDs and server messages. Use stable HTTP dimensions.
        ...(event.fingerprint ?? []).filter((part) => part !== "{{ default }}"),
        "http",
        String(status),
        method ?? "unknown",
        endpoint ?? event.transaction ?? "unknown",
      ],
      contexts: {
        ...event.contexts,
        api_response: {
          status,
          url,
          method,
          endpoint,
          error: payload,
        },
      },
    };
  }

  return event;
}

export function filterSentryError(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const error = hint.originalException;
  const isNotFound =
    error === 404 ||
    ((error instanceof Response || isHttpError(error) || isRouteErrorResponse(error)) && error.status === 404);
  if (isNotFound) {
    const isApiError =
      !isRouteErrorResponse(error) && (isHttpError(error) || (error instanceof Response && !!error.url));
    // Missing routes are expected; keep a 10% sample of API 404s for broken-flow diagnostics.
    if (!isApiError || Math.random() >= 0.1) {
      return null;
    }
  }

  return event;
}

export function filterSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category !== "fetch" && breadcrumb.category !== "xhr") {
    return breadcrumb;
  }

  const breadcrumbUrl = breadcrumb.data?.url;
  if (typeof breadcrumbUrl !== "string") {
    return breadcrumb;
  }

  try {
    const pathname = new URL(breadcrumbUrl, window.location.origin).pathname;

    return signalRHubPathPattern.test(pathname) ? null : breadcrumb;
  } catch {
    return breadcrumb;
  }
}

export function initializeSentry(): void {
  Sentry.init({
    dsn: import.meta.env.DEV
      ? undefined
      : "https://b54dbf1a9ece40a9a9339c6d64731c92@o4504289700610048.ingest.sentry.io/4504289709260800",
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect: useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes: matchSentryRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    attachStacktrace: true,
    beforeBreadcrumb: filterSentryBreadcrumb,
    // oxlint-disable-next-line typescript/no-base-to-string
    release: window.AOP_VERSION ? String(window.AOP_VERSION) : undefined,
    // oxlint-disable-next-line typescript/no-base-to-string
    environment: window.SENTRY_ENVIRONMENT ? String(window.SENTRY_ENVIRONMENT) : undefined,
    beforeSend(event, hint) {
      if (import.meta.env.DEV) {
        console.error(hint.originalException || hint.syntheticException);

        return null;
      }

      const filteredEvent = filterSentryError(event, hint);

      return filteredEvent ? enrichSentryError(filteredEvent, hint) : null;
    },
  });
}
